import {
  ALL_DIAGNOSTIC_CODES,
  ALL_REPAIR_RULES,
  EN_MESSAGES,
  type MessageCatalog,
  type MessageCode,
} from '@jsonfmt/core';
import type { i18n as I18nInstance } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { MessageResource } from '@/locales';

/**
 * THE INVERSION SEAM between i18next and the engine (ADR-0015, ADR-0018).
 *
 * The engine never imports i18next — a `MessageCatalog` is DATA a caller supplies, which is
 * what lets the engine run in a Worker, in a CLI or in a bare-Node test. This file is the
 * one place where the dependency is inverted: it takes i18next's resources and hands the
 * engine a plain `Readonly<Record<MessageCode, string>>`.
 *
 * Two directions, and both are needed:
 *
 *   toMessageResource(EN_MESSAGES)  engine → i18next, at init time. ADR-0018's "no
 *                                   duplication": the English templates have exactly one
 *                                   home (`core/messages/en.ts`) and are SPREAD into the
 *                                   resources, never copied into `common.json`.
 *   createMessageCatalog(lookup)    i18next → engine, at render time. A translated locale
 *                                   overrides the templates; anything it does not carry
 *                                   falls back to `EN_MESSAGES` rather than to a raw code.
 *
 * ── Why the `fix.` branch is nested ──────────────────────────────────────────────────────
 *
 * i18next splits keys on `.`. A `DiagnosticCode` is namespaced with `/`
 * (`syntax/unexpected-token`) and so survives as a flat leaf, but a `FixLabelCode` is
 * literally `fix.${RepairRuleId}` — as a flat key under a `.` separator it is unreachable:
 * i18next would look for `message` → `fix` → `strip-bom` and find nothing. So the `fix.`
 * family is nested into a real object, once, here.
 */

/** The branch of the `common` namespace that belongs to the engine. */
export const MESSAGE_BRANCH = 'message';

/** i18next's key separator. Its default, named so the two halves of this file agree. */
const KEY_SEPARATOR = '.';

/**
 * Every code the engine can ask to have rendered, derived from the engine's own runtime
 * lists rather than from `EN_MESSAGES`.
 *
 * That independence is the point: `Object.keys(EN_MESSAGES)` would agree with itself no
 * matter what, whereas this list fails the moment a diagnostic code or a repair rule exists
 * that the catalog does not cover.
 */
export const ALL_MESSAGE_CODES: readonly MessageCode[] = [
  ...ALL_DIAGNOSTIC_CODES,
  ...ALL_REPAIR_RULES.map((rule): MessageCode => `fix.${rule.id}`),
];

/** The full i18next key for one code, e.g. `message.fix.strip-bom`. */
export function messageKey(code: MessageCode): string {
  return `${MESSAGE_BRANCH}${KEY_SEPARATOR}${code}`;
}

/**
 * Shape a flat `MessageCatalog` into the nested object i18next can address.
 *
 * Splits on the FIRST `.` only: `fix.remove-trailing-comma` becomes
 * `{ fix: { 'remove-trailing-comma': … } }` and a rule id that ever contained a dot would
 * still land in one bucket rather than silently vanishing.
 */
export function toMessageResource(catalog: MessageCatalog = EN_MESSAGES): MessageResource {
  const out: Record<string, string | Record<string, string>> = {};

  for (const [code, template] of Object.entries(catalog)) {
    const separator = code.indexOf(KEY_SEPARATOR);
    if (separator === -1) {
      out[code] = template;
      continue;
    }
    const head = code.slice(0, separator);
    const tail = code.slice(separator + KEY_SEPARATOR.length);
    const existing = out[head];
    const bucket = typeof existing === 'object' && existing !== null ? existing : {};
    bucket[tail] = template;
    out[head] = bucket;
  }

  return out as unknown as MessageResource;
}

/**
 * Read one raw TEMPLATE — `{{token}}` intact — for a full i18next key.
 *
 * Deliberately NOT `t()`. `t()` interpolates, and the engine's `renderMessage` is what does
 * the interpolation here (it owns the "leave an unmatched placeholder in place" rule, which
 * is how a params/template mismatch stays greppable instead of turning into a grammatical
 * sentence that is quietly wrong). Asking `t()` for a template and getting an interpolated
 * string back would destroy exactly the information the catalog is supposed to carry.
 */
export type TemplateLookup = (key: string) => string | undefined;

/**
 * Build the engine's catalog.
 *
 * Missing, blank or non-string → the English template. Never the raw code: a user seeing
 * `number/leading-zero` in a tooltip is the failure this whole layer exists to prevent, and
 * a locale is allowed to be incomplete without taking the diagnostics panel down with it.
 */
export function createMessageCatalog(lookup: TemplateLookup): MessageCatalog {
  const entries: [MessageCode, string][] = ALL_MESSAGE_CODES.map((code) => {
    const raw = lookup(messageKey(code));
    const usable = typeof raw === 'string' && raw.trim().length > 0;
    return [code, usable ? raw : EN_MESSAGES[code]];
  });
  return Object.fromEntries(entries) as MessageCatalog;
}

/** What {@link messageCatalogOf} needs from an i18next instance, and nothing more. */
export type ResourceReader = Pick<I18nInstance, 'getResource' | 'language' | 'resolvedLanguage'>;

/** {@link createMessageCatalog} bound to a live i18next instance. */
export function messageCatalogOf(i18n: ResourceReader, ns = 'common'): MessageCatalog {
  const lng = i18n.resolvedLanguage ?? i18n.language;
  return createMessageCatalog((key) => {
    const value: unknown = i18n.getResource(lng, ns, key);
    return typeof value === 'string' ? value : undefined;
  });
}

/**
 * The React binding. Memoised on the resolved language, so the catalog is one stable object
 * per language — `renderDiagnostic` is called once per visible diagnostic per keystroke, and
 * a fresh 53-entry object each time would be the kind of cost nobody ever goes looking for.
 */
export function useMessageCatalog(): MessageCatalog {
  const { i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? i18n.language;
  return useMemo(() => messageCatalogOf(i18n), [i18n, lng]);
}
